"use client";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef,useState } from "react";

import { useAuthToken } from "@/lib/auth/useAuthToken";

import { type MenuId, MENUS, type NavIcon,type NavLink } from "./_data/nav";

// ─────────────────────────────────────────────────────────────────────────────
// Primary navigation.
//
// The menu *content* lives in ./_data/nav.ts — six mega menus, every href
// checked against the real route tree. This file owns the *behaviour* only:
// hover + click mega menus, ESC to close, outside-click to close, active-section
// highlighting, the mobile drawer with its focus trap, body-scroll lock and
// accordion sections.
//
// WHY FIVE MENUS
//   The IA brief asked for a separate "AI Capabilities" and "Legal
//   Intelligence" menu. Seven triggers plus the logo plus three right-side
//   actions do not fit a 1366px bar without wrapping, which fails the brief's
//   own "do not overcrowd the navbar" rule. The two AI menus are merged into
//   "Intelligence", which carries both groups as labelled column sets.
//   The India build's sixth menu ("Practice areas", 13 India-statute hubs)
//   was removed for the global site — see the note above the Intelligence
//   entry in ./_data/nav.ts.
//
// PRICING
//   /pricing exists as a route but is deliberately hidden — it is excluded from
//   src/app/sitemap.ts and absent from the footer. Restoring it is a one-line
//   change in each of the three marked spots below. Keep all three in sync.
//
// WHY THIS IS A DISCLOSURE, NOT A role="menu"
//   The panels used to carry role="menu" / role="menuitem". That is the
//   desktop-application menu pattern: it promises arrow-key roving focus that
//   this component does not implement, and it only permits menuitems as
//   children — so the featured card's prose was silently dropped from the
//   accessibility tree. Site navigation is a disclosure (WAI-ARIA APG): a
//   button with aria-expanded/aria-controls revealing a plain list of links.
//
// WHY THE PANELS OPEN FROM STATE AND NOT FROM :hover
//   Opening used to be a CSS `.nav-item:hover .mega` rule racing a React
//   `is-open` class. Two owners of one piece of state, and no intent delay, so
//   dragging the cursor across the bar fired three panels in sequence. Hover
//   now sets the same `openMenu` state the keyboard uses, behind the delays
//   below, and CSS only reacts to `.is-open`.
// ─────────────────────────────────────────────────────────────────────────────

/** Hover-intent delays, ms. Open is short enough to feel instant on a
 *  deliberate hover; close is long enough to survive the diagonal cut across
 *  a neighbouring trigger on the way to the panel. */
const OPEN_DELAY = 110;
const CLOSE_DELAY = 240;

/** Icon glyphs for spotlight columns. Paths only — the <svg> wrapper, sizing
 *  and stroke live in renderLink so every tile is identical. */
const ICONS: Record<NavIcon, React.ReactNode> = {
  overview: (
    <>
      <rect height="7" rx="1.5" width="7" x="3.5" y="3.5" />
      <rect height="7" rx="1.5" width="7" x="13.5" y="3.5" />
      <rect height="7" rx="1.5" width="7" x="3.5" y="13.5" />
      <rect height="7" rx="1.5" width="7" x="13.5" y="13.5" />
    </>
  ),
  legal: <path d="M12 3.5V20M9 20h6M3.5 7.5h17M4 7.5 1.5 13h5zM20 7.5 17.5 13h5z" />,
  compliance: (
    <>
      <path d="M12 2.5 19 5.5v6c0 4.5-2.9 7.8-7 9.5-4.1-1.7-7-5-7-9.5v-6z" />
      <path d="m9 11.5 2 2 4-4" />
    </>
  ),
  tax: (
    <>
      <path d="M6.5 17.5 17.5 6.5" />
      <circle cx="8.5" cy="8.5" r="2.2" />
      <circle cx="15.5" cy="15.5" r="2.2" />
    </>
  ),
  agents: <path d="M13 2.5 4.5 14h7l-1 7.5L20 10h-7z" />,
  global: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z" />
    </>
  ),
};

export default function Navbar() {
  const pathname = usePathname() || "/";
  // Signed-in visitors keep the whole marketing site — they just get a way
  // back into the product instead of the three acquisition CTAs. `null`
  // during SSR and the hydration pass (useSyncExternalStore's server
  // snapshot), so the signed-out bar is what renders first either way.
  const signedIn = useAuthToken() !== null;
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState<MenuId | null>(null);
  const [openMenu, setOpenMenu] = useState<MenuId | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const drawerRef = useRef<HTMLDivElement>(null);
  const navRef = useRef<HTMLElement>(null);
  const hamRef = useRef<HTMLButtonElement>(null);
  const hoverTimer = useRef<number | null>(null);

  // Scroll state (drives drawer offset + nav shadow in CSS)
  useEffect(() => {
    function onScroll() { setScrolled(window.scrollY > 20); }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile drawer on outside click (skip the hamburger button itself)
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Element | null;
      if (!target) return;
      if (target.closest(".nav-ham")) return;
      if (drawerRef.current && !drawerRef.current.contains(target as Node)) {
        setMobileOpen(false);
      }
    }
    if (mobileOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [mobileOpen]);

  // Close keyboard-opened mega menu on outside click or Escape
  useEffect(() => {
    function handleDown(e: MouseEvent) {
      const target = e.target as Element | null;
      if (!target) return;
      if (navRef.current && !navRef.current.contains(target as Node)) {
        setOpenMenu(null);
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setOpenMenu(null); setMobileOpen(false); }
    }
    document.addEventListener("mousedown", handleDown);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleDown);
      document.removeEventListener("keydown", handleKey);
    };
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Focus trap for mobile drawer — move focus in on open, trap Tab inside,
  // restore focus to hamburger on close.
  useEffect(() => {
    if (!mobileOpen) return;
    const drawer = drawerRef.current;
    if (!drawer) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // Capture the hamburger node now: reading hamRef.current in the cleanup
    // trips react-hooks/exhaustive-deps, and the node is stable while open.
    const hamburger = hamRef.current;
    const getFocusable = () => Array.from(
      drawer.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => !el.hasAttribute("aria-hidden") && el.offsetParent !== null);

    const first = getFocusable()[0];
    first?.focus();

    function onKey(e: KeyboardEvent) {
      if (e.key !== "Tab") return;
      const items = getFocusable();
      if (items.length === 0) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      (previouslyFocused ?? hamburger)?.focus?.();
    };
  }, [mobileOpen]);

  function toggleSection(id: MenuId) {
    setMobileExpanded(prev => (prev === id ? null : id));
  }

  function toggleMenu(id: MenuId) {
    clearHoverTimer();
    setOpenMenu(prev => (prev === id ? null : id));
  }

  // ── hover intent ──────────────────────────────────────────────────────────
  function clearHoverTimer() {
    if (hoverTimer.current !== null) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  }

  function onItemEnter(id: MenuId) {
    clearHoverTimer();
    // Once a panel is already open, moving along the bar switches instantly —
    // the reader has committed to browsing the nav. The delay only exists to
    // stop a panel firing at a cursor that is merely passing through.
    if (openMenu !== null) { setOpenMenu(id); return; }
    hoverTimer.current = window.setTimeout(() => setOpenMenu(id), OPEN_DELAY);
  }

  function onItemLeave() {
    clearHoverTimer();
    hoverTimer.current = window.setTimeout(() => setOpenMenu(null), CLOSE_DELAY);
  }

  useEffect(() => clearHoverTimer, []);

  // ArrowDown on a trigger opens its panel and drops focus onto the first link,
  // matching the WAI-ARIA disclosure-navigation pattern.
  function onTriggerKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, id: MenuId) {
    if (e.key !== "ArrowDown") return;
    e.preventDefault();
    setOpenMenu(id);
    const panel = document.getElementById(`mega-${id}`);
    // Wait a frame so the panel is visible (and therefore focusable) first.
    requestAnimationFrame(() => panel?.querySelector<HTMLElement>("a[href]")?.focus());
  }

  // Active state helpers
  const isActive = (href: string) => {
    if (href === "/") return pathname === "/";
    return pathname === href || pathname.startsWith(href + "/");
  };
  const menuActive = (prefixes: ReadonlyArray<string>) =>
    prefixes.some(p => pathname === p || pathname.startsWith(p + "/"));

  function renderLink(l: NavLink, spotlight?: boolean) {
    const active = isActive(l.href);
    const icon = spotlight && l.icon ? ICONS[l.icon] : null;
    return (
      <li key={l.href + l.label}>
        <Link
          aria-current={active ? "page" : undefined}
          className={`mega-nav-link ${icon ? "has-ico" : ""} ${active ? "is-active" : ""}`}
          href={l.href}
          prefetch={false}
          onClick={() => setOpenMenu(null)}
        >
          {icon && (
            <span aria-hidden="true" className="mega-nav-ico">
              <svg fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.6" viewBox="0 0 24 24">{icon}</svg>
            </span>
          )}
          <span className="mega-nav-body">
            <span className="mega-nav-name">
              {l.label}
              {l.tag && <span className="mega-nav-tag">{l.tag}</span>}
            </span>
            {l.desc && <span className="mega-nav-desc">{l.desc}</span>}
          </span>
        </Link>
      </li>
    );
  }

  return (
    <>
<a className="skip-link" href="#main">Skip to content</a>
<nav ref={navRef} id="nav">
  {/* Deliberately a raw <a>, not next/link: ReloadOnBack hard-reloads any
      client-side navigation that lands on "/" (the homepage animations need a
      fresh mount), so a Link here would cost an SPA transition PLUS a reload.
      One document load is the cheaper path. */}
  {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
  <a aria-label="LexVio home" className="logo" href="/" style={{display:"inline-flex",alignItems:"center"}}><Image priority alt="LexVio.ai" height={40} src="/logo.png" style={{display:"block",height:"40px",width:"107px"}} width={107} /></a>
  <div className="nav-c">

    {MENUS.map((m) => {
      const open = openMenu === m.id;
      // Panel width and grid both fall out of this one number, so every panel
      // in the bar ends up with identical column widths. See nav.ts.
      const cols = m.columns.length + (m.featured ? 1 : 0);
      return (
      <div
        key={m.id}
        className={`nav-item ${m.anchor === "bar" ? "nav-item-wide" : ""} ${open ? "is-open" : ""} ${menuActive(m.activePrefixes) ? "is-active" : ""}`}
        onMouseEnter={() => onItemEnter(m.id)}
        onMouseLeave={onItemLeave}
      >
        <button
          aria-controls={`mega-${m.id}`}
          aria-expanded={open}
          className="nav-btn"
          id={`mega-trigger-${m.id}`}
          type="button"
          onClick={() => toggleMenu(m.id)}
          onKeyDown={(e) => onTriggerKeyDown(e, m.id)}
        >{m.label}
          <svg aria-hidden="true" className="nav-chevron" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 10 10"><polyline points="2,3 5,7 8,3"/></svg>
        </button>

        {/* A closed panel is only transparent, not unmounted — it has to stay in
            the DOM to animate. `inert` is what keeps its ~25 links out of the
            tab order and the accessibility tree until it actually opens. */}
        <div
          aria-labelledby={`mega-trigger-${m.id}`}
          className={`mega mega-nav ${m.anchor === "right" ? "mega-right" : ""}`}
          id={`mega-${m.id}`}
          inert={!open}
          style={{ "--mega-cols": cols } as React.CSSProperties}
        >
          <div className="mega-nav-grid">
            {m.columns.map((col, ci) => {
              const titleId = `mega-${m.id}-c${ci}`;
              return (
                <div key={col.title} className={`mega-col ${col.spotlight ? "is-spotlight" : ""}`}>
                  <h3 className="mega-col-title" id={titleId}>{col.title}</h3>
                  <ul aria-labelledby={titleId} className="mega-nav-list">
                    {col.links.map((l) => renderLink(l, col.spotlight))}
                  </ul>
                </div>
              );
            })}
            {m.featured && (
              <div className="mega-feature">
                <span className="mega-feature-eyebrow">{m.featured.eyebrow}</span>
                <p className="mega-feature-line">{m.featured.line}</p>
                <div className="mega-feature-actions">
                  <Link className="mega-feature-cta" href={m.featured.cta.href} prefetch={false} onClick={() => setOpenMenu(null)}>{m.featured.cta.label}<span aria-hidden="true">&rarr;</span></Link>
                  {m.featured.secondary && (
                    <Link className="mega-feature-sec" href={m.featured.secondary.href} prefetch={false} onClick={() => setOpenMenu(null)}>{m.featured.secondary.label}<span aria-hidden="true">&rarr;</span></Link>
                  )}
                </div>
              </div>
            )}
          </div>
          {m.footer && (
            <div className="mega-footer">
              <Link className="mega-footer-link" href={m.footer.link.href} prefetch={false} onClick={() => setOpenMenu(null)}>{m.footer.link.label}<span aria-hidden="true">&rarr;</span></Link>
              {m.footer.tag && <span className="mega-footer-tag">{m.footer.tag}</span>}
            </div>
          )}
        </div>
      </div>
      );
    })}

  </div>

  <div className="nav-r">
    {/* Pricing is intentionally absent — /pricing is hidden by decision and is
        excluded from src/app/sitemap.ts and from the footer. When pricing
        returns, uncomment this link, the mobile one below, and re-add the
        footer + sitemap entries.
    <a
      href="/pricing"
      className={`nl ${isActive("/pricing") ? "is-active" : ""}`}
      aria-current={isActive("/pricing") ? "page" : undefined}
    >Pricing</a> */}
    {signedIn ? (
      <a className="nav-cta" href={`/dashboard`}><span>Dashboard &rarr;</span></a>
    ) : (
      <>
        <a className="nav-in" href={`/sign-in`}>Sign in</a>
        <Link className="nav-demo" href="/book-demo" prefetch={false}>Book demo</Link>
        <a className="nav-cta" href={`/sign-up`}><span>Get started &rarr;</span></a>
      </>
    )}
    <button
      ref={hamRef}
      aria-controls="mob-drawer"
      aria-expanded={mobileOpen}
      aria-label={mobileOpen ? "Close menu" : "Open menu"}
      className="nav-ham"
      onClick={() => setMobileOpen(o => !o)}
    >
      <span className={`ham-line ${mobileOpen ? "ham-open" : ""}`} />
      <span className={`ham-line ${mobileOpen ? "ham-open" : ""}`} />
      <span className={`ham-line ${mobileOpen ? "ham-open" : ""}`} />
    </button>
  </div>
</nav>

{mobileOpen && (
  <div
    aria-hidden="true"
    style={{position:"fixed",inset:0,background:"rgba(10,10,9,.5)",zIndex:699,backdropFilter:"blur(4px)"}}
    onClick={() => setMobileOpen(false)}
  />
)}

<div
  ref={drawerRef}
  className={`mob-drawer ${mobileOpen ? "mob-drawer-open" : ""} ${scrolled ? "scrolled-offset" : ""}`}
  id="mob-drawer"
  aria-hidden={!mobileOpen}
  /* The drawer sits off-canvas rather than unmounted, so its links stayed in
     the tab order while hidden. inert takes them out of it (and out of the a11y
     tree) until the drawer is actually open. */
  inert={!mobileOpen}
>
  <div className="mob-drawer-inner">

    {/* The brief specifies a drawer header with an explicit close control.
        It also matters for the focus trap: without an in-drawer close, a
        keyboard user who tabs into the drawer can only leave it via ESC. */}
    <div className="mob-drawer-head">
      <span className="mob-drawer-brand"><Image alt="LexVio.ai" height={28} src="/logo.png" style={{display:"block",height:"28px",width:"75px"}} width={75} /></span>
      <button
        aria-label="Close menu"
        className="mob-drawer-close"
        type="button"
        onClick={() => setMobileOpen(false)}
      >
        <svg aria-hidden="true" fill="none" height="18" viewBox="0 0 24 24" width="18">
          <path d="M18 6 6 18M6 6l12 12" stroke="currentColor" strokeLinecap="round" strokeWidth="1.6" />
        </svg>
      </button>
    </div>

    {MENUS.map((m) => (
      <div key={m.id} className="mob-section">
        <button
          aria-controls={`mob-${m.id}`}
          aria-expanded={mobileExpanded === m.id}
          className="mob-section-btn"
          onClick={() => toggleSection(m.id)}
        >
          {m.label}
          <svg aria-hidden="true" className={`mob-chevron ${mobileExpanded === m.id ? "mob-chevron-open" : ""}`} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 10 10"><polyline points="2,3 5,7 8,3"/></svg>
        </button>
        {mobileExpanded === m.id && (
          <div className="mob-links" id={`mob-${m.id}`}>
            {m.columns.map((col) => (
              <div key={col.title}>
                <div className="mob-group-title">{col.title}</div>
                {col.links.map((l) => (
                  <Link
                    key={l.href + l.label}
                    aria-current={isActive(l.href) ? "page" : undefined}
                    className={`mob-link ${isActive(l.href) ? "is-active" : ""}`}
                    href={l.href}
                    prefetch={false}
                    onClick={() => setMobileOpen(false)}
                  >
                    {l.label}
                    {l.tag && <span className="mega-nav-tag">{l.tag}</span>}
                  </Link>
                ))}
              </div>
            ))}
            {m.footer && (
              <Link
                className="mob-link"
                href={m.footer.link.href}
                prefetch={false}
                style={{color:"var(--cu-ink)"}}
                onClick={() => setMobileOpen(false)}
              >{m.footer.link.label} &rarr;</Link>
            )}
          </div>
        )}
      </div>
    ))}

    {/* Pricing is intentionally absent — see the note beside the desktop link.
    <a
      href="/pricing"
      className={`mob-top-link ${isActive("/pricing") ? "is-active" : ""}`}
      aria-current={isActive("/pricing") ? "page" : undefined}
      onClick={() => setMobileOpen(false)}
    >Pricing</a> */}

    <div className="mob-ctas">
      {signedIn ? (
        <a className="nav-cta" href={`/dashboard`} style={{display:"block",textAlign:"center"}} onClick={() => setMobileOpen(false)}><span>Dashboard &rarr;</span></a>
      ) : (
        <>
          <a className="mob-signin" href={`/sign-in`} onClick={() => setMobileOpen(false)}>Sign in</a>
          <Link className="mob-demo" href="/book-demo" prefetch={false} onClick={() => setMobileOpen(false)}>Book demo</Link>
          <a className="nav-cta" href={`/sign-up`} style={{display:"block",textAlign:"center"}} onClick={() => setMobileOpen(false)}><span>Get started &rarr;</span></a>
        </>
      )}
    </div>

  </div>
</div>

    </>
  );
}
